import { useCallback, useRef } from 'react';

/**
 * Inserts `{{variableKey}}` into message text at the textarea caret,
 * or at the end when the textarea is not focused. Keeps focus/selection after insert.
 */
export function useInsertVariableAtCursorPosition() {
    const textareaRefs = useRef<Map<number | string, HTMLTextAreaElement | HTMLInputElement>>(new Map());

    const registerTextarea = useCallback((msgIndex: number | string, element: HTMLTextAreaElement | HTMLInputElement | null) => {
        if (element) {
            textareaRefs?.current?.set(msgIndex, element);
        } else {
            textareaRefs?.current?.delete(msgIndex);
        }
    }, []);

    const insertVariable = useCallback((variableKey: string, msgIndex: number | string, msgContentBeforeInsert: string) => {
            const msgTextarea = textareaRefs?.current?.get(msgIndex);
            const variablePlaceholder = `{{${variableKey}}}`;

            let selectionStartIndex = msgContentBeforeInsert?.length;
            let selectionEndIndex = msgContentBeforeInsert?.length;

            if (msgTextarea && document?.activeElement === msgTextarea) {
                selectionStartIndex = msgTextarea?.selectionStart ?? 0;
                selectionEndIndex = msgTextarea?.selectionEnd ?? 0;
            }

            const msgContentAfterInsert = msgContentBeforeInsert?.slice(0, selectionStartIndex) + variablePlaceholder + msgContentBeforeInsert?.slice(selectionEndIndex);

            // Set Caret Position
            // Case 1: When variableKey is empty -> Set Caret Position between curly brackets
            // Case 2: When variableKey is not empty -> Set Caret Position just after variable
            const caretPositionAfterVariable = selectionStartIndex + (variableKey?.length === 0 ? 2 : variablePlaceholder?.length);

            requestAnimationFrame(() => {
                const textareaToRestore = textareaRefs?.current?.get(msgIndex);
                if (textareaToRestore) {
                    textareaToRestore?.focus();
                    textareaToRestore?.setSelectionRange(caretPositionAfterVariable, caretPositionAfterVariable);
                }
            });

            return msgContentAfterInsert;
        },[]
    );

    return { registerTextarea, insertVariable };
}